import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, 
  Sparkles, 
  Heart, 
  MessageSquare, 
  FileText,
  Copy,
  Check,
  User,
  Calendar,
  Users,
  Stethoscope,
  Church,
  Briefcase,
  Star,
  MessageCircle,
  Lightbulb
} from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Quiltt Card section configuration
const CARE_CARD_SECTIONS = [
  { key: 'background', label: 'Background', icon: User, color: 'blue' },
  { key: 'family', label: 'Family', icon: Users, color: 'purple' },
  { key: 'careerHighlights', label: 'Career Highlights', icon: Briefcase, color: 'amber' },
  { key: 'importantDates', label: 'Important Dates', icon: Calendar, color: 'rose' },
  { key: 'preferences', label: 'Preferences', icon: Star, color: 'teal' },
  { key: 'healthNotes', label: 'Health Notes', icon: Stethoscope, color: 'green' },
  { key: 'spiritualCultural', label: 'Spiritual & Cultural', icon: Church, color: 'indigo' },
  { key: 'communicationTips', label: 'Communication Tips', icon: MessageCircle, color: 'orange' }
];

const COLOR_CLASSES = {
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600', accent: 'bg-blue-100' },
  purple: { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'text-purple-600', accent: 'bg-purple-100' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-600', accent: 'bg-amber-100' },
  rose: { bg: 'bg-rose-50', border: 'border-rose-200', icon: 'text-rose-600', accent: 'bg-rose-100' },
  teal: { bg: 'bg-teal-50', border: 'border-teal-200', icon: 'text-teal-600', accent: 'bg-teal-100' },
  green: { bg: 'bg-green-50', border: 'border-green-200', icon: 'text-green-600', accent: 'bg-green-100' },
  indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', icon: 'text-indigo-600', accent: 'bg-indigo-100' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'text-orange-600', accent: 'bg-orange-100' }
};

export default function FamilySummariesView() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [story, setStory] = useState(null);
  const [summaries, setSummaries] = useState(null);
  const [activeTab, setActiveTab] = useState('careCard');
  const [copied, setCopied] = useState(null);

  // Cloud Functions
  const functions = getFunctions();
  const loadFamilyPortalFn = httpsCallable(functions, 'loadFamilyPortal');

  useEffect(() => {
    loadData();
  }, [token]);

  const loadData = async () => {
    try {
      // =============================================
      // SECURITY FIX: Use Cloud Function instead of
      // direct Firestore reads. Family members are
      // unauthenticated — Firestore rules block
      // direct reads on lifeStories.
      // =============================================
      const result = await loadFamilyPortalFn({ portalToken: token });
      
      if (!result.data.success) {
        setError('Failed to load summaries.');
        setLoading(false);
        return;
      }

      const { portal: portalData, story: storyData } = result.data;
      
      // Check permissions
      if (!portalData.permissions?.viewSummaries) {
        setError('You do not have permission to view summaries.');
        setLoading(false);
        return;
      }
      
      setStory(storyData);
      setSummaries(storyData.aiSummaries || null);
      
      // Set initial tab based on available content
      if (storyData.aiSummaries) {
        if (storyData.aiSummaries.careCard && Object.keys(storyData.aiSummaries.careCard).length > 0) {
          setActiveTab('careCard');
        } else if (storyData.aiSummaries.conversationStarters?.length > 0) {
          setActiveTab('conversationStarters');
        } else if (storyData.aiSummaries.interests?.length > 0) {
          setActiveTab('interests');
        }
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error loading summaries:', err);
      if (err.code === 'not-found') {
        setError('Invalid or expired link.');
      } else {
        setError('Failed to load summaries.');
      }
      setLoading(false);
    }
  };

  const getDisplayName = () => {
    if (!story) return '';
    const name = story.subjectName || story.preferredName || story.residentName || '';
    if (story.coupleMode && story.partnerName) {
      return `${name} & ${story.partnerName}`;
    }
    return name;
  };

  const getFirstName = () => {
    const name = getDisplayName();
    return name.split(' ')[0] || name;
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-quiltt-purple border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">Loading summaries...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-red-500" />
          </div>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(`/family/${token}`)}
            className="px-6 py-3 bg-quiltt-purple text-white rounded-xl font-medium hover:bg-purple-700 transition-colors"
          >
            Back to Portal
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'careCard', label: 'Quiltt Card', icon: Heart, show: summaries?.careCard && Object.keys(summaries.careCard).length > 0 },
    { id: 'conversationStarters', label: 'Conversation Starters', icon: MessageSquare, show: summaries?.conversationStarters?.length > 0 },
    { id: 'interests', label: 'Interests', icon: Sparkles, show: summaries?.interests?.length > 0 }
  ].filter(tab => tab.show);

  // Format conversation starters - handle both array of objects and array of strings
  const getConversationStarters = () => {
    if (!summaries?.conversationStarters) return [];
    return summaries.conversationStarters.map((item, index) => {
      if (typeof item === 'string') {
        return { topic: `Topic ${index + 1}`, question: item, context: '' };
      }
      return item;
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`/family/${token}`)}
              className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex-1">
              <h1 className="font-bold text-gray-900 text-lg">AI Summaries</h1>
              <p className="text-sm text-gray-500">{getDisplayName()}</p>
            </div>
            {story?.profilePhotoUrl && (
              <img 
                src={story.profilePhotoUrl} 
                alt="" 
                className="w-10 h-10 rounded-full object-cover border-2 border-purple-200"
              />
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {!summaries || tabs.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-10 h-10 text-quiltt-purple" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-3">No Summaries Yet</h2>
            <p className="text-gray-600 max-w-sm mx-auto">
              AI summaries haven't been generated for this story yet. 
              Check back later or ask the story owner to generate them.
            </p>
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2 scrollbar-hide">
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium whitespace-nowrap transition-all ${
                      activeTab === tab.id
                        ? 'bg-quiltt-purple text-white shadow-lg shadow-purple-200'
                        : 'bg-white text-gray-600 hover:bg-gray-50 shadow-sm'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Quiltt Card */}
            {activeTab === 'careCard' && summaries.careCard && (
              <div className="space-y-4">
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-red-400 to-pink-500 rounded-xl flex items-center justify-center">
                      <Heart className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">{getFirstName()}'s Quiltt Card</h2>
                      <p className="text-sm text-gray-500">Essential information for caregivers</p>
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    {CARE_CARD_SECTIONS.map(section => {
                      const content = summaries.careCard[section.key];
                      if (!content) return null;
                      
                      const Icon = section.icon;
                      const colors = COLOR_CLASSES[section.color];
                      
                      return (
                        <div 
                          key={section.key}
                          className={`${colors.bg} ${colors.border} border rounded-xl p-4`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`w-8 h-8 ${colors.accent} rounded-lg flex items-center justify-center`}>
                              <Icon className={`w-4 h-4 ${colors.icon}`} />
                            </div>
                            <h3 className="font-semibold text-gray-900">{section.label}</h3>
                          </div>
                          <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
                            {content}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Conversation Starters */}
            {activeTab === 'conversationStarters' && (
              <div className="space-y-4">
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-blue-100">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center">
                      <MessageSquare className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Conversation Starters</h2>
                      <p className="text-sm text-gray-500">Great topics to discuss with {getFirstName()}</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {getConversationStarters().map((starter, index) => (
                      <div
                        key={index}
                        className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-100"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            {starter.topic && (
                              <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full mb-2">
                                {starter.topic}
                              </span>
                            )}
                            <p className="text-gray-900 font-medium text-lg mb-2">
                              "{starter.question}"
                            </p>
                            {starter.context && (
                              <p className="text-gray-600 text-sm flex items-start gap-2">
                                <Lightbulb className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                                {starter.context}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Interests */}
            {activeTab === 'interests' && summaries.interests?.length > 0 && (
              <div className="space-y-4">
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-amber-100">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
                      <Sparkles className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">{getFirstName()}'s Interests</h2>
                      <p className="text-sm text-gray-500">Topics and activities they enjoy</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    {summaries.interests.map((interest, index) => (
                      <span
                        key={index}
                        className="px-5 py-2.5 bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 rounded-full font-medium text-sm border border-amber-200 hover:shadow-md transition-shadow"
                      >
                        {interest}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Footer note */}
            <div className="mt-8 text-center">
              <p className="text-sm text-gray-500 flex items-center justify-center gap-2">
                <Sparkles className="w-4 h-4" />
                Generated from {getFirstName()}'s recorded memories using AI
              </p>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
